import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import ApiService from '../../services/ApiService';
import Loader from '../../components/Loader';
import { formatDate } from '../../utils/helper';

const EmiratesList = () => {
  const [emirates, setEmirates] = useState([]);
  const [isLoader, setIsLoader] = useState(false);
  const [pageLoader, setPageLoader] = useState(false);
  const [apiData, setApiData] = useState({
    page: 1,
    limit: 20,
    search: '',
    sort: 'name',
    order: 'asc'
  });

  const statusBadgeMap = {
    'Active': 'bg-success',
    'Inactive': 'bg-danger'
  };

  useEffect(() => {
    getEmirates();
  }, [apiData.page, apiData.limit, apiData.search, apiData.sort, apiData.order]);

  const getEmirates = async () => {
    try {
      setPageLoader(true);
      const response = await ApiService.request({
        method: 'GET',
        url: `emirates?page=${apiData.page}&limit=${apiData.limit}&search=${apiData.search}&sort=${apiData.sort}&order=${apiData.order}`,
      });

      const data = response.data;
      if (data.status) {
        setEmirates(data.data);
      } else {
        toast.error(data.message);
      }
      setPageLoader(false);
    } catch (error) {
      console.log(error);
      setPageLoader(false);
    }
  };

  const updateStatus = async (id) => {
    try {
      setIsLoader(true);
      const response = await ApiService.request({
        method: 'POST',
        url: `emirates/status/${id}`,
        data: { status: true }
      });

      const data = response.data;
      if (data.status) {
        toast.success(data.message);
        getEmirates();
      } else {
        toast.error(data.message);
      }
      setIsLoader(false);
    } catch (error) {
      setIsLoader(false);
      toast.error('Error updating status');
    }
  };

  const deleteEmirate = async (id) => {
    if (window.confirm('Are you sure you want to delete this emirate?')) {
      try {
        setIsLoader(true);
        const response = await ApiService.request({
          method: 'DELETE',
          url: `emirates/${id}`,
        });

        const data = response.data;
        if (data.status) {
          toast.success(data.message);
          getEmirates();
        } else {
          toast.error(data.message);
        }
        setIsLoader(false);
      } catch (error) {
        setIsLoader(false);
        toast.error('Error deleting emirate');
      }
    }
  };

  return (
    <div className="d-flex flex-column flex-column-fluid">
      <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
        <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
          <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
            <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
              Emirates Management
            </h1>
            <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
              <li className="breadcrumb-item text-muted">
                <Link to="/dashboard" className="text-muted text-hover-primary">Home</Link>
              </li>
              <li className="breadcrumb-item">
                <span className="bullet bg-gray-400 w-5px h-2px"></span>
              </li>
              <li className="breadcrumb-item text-muted">Emirates</li>
            </ul>
          </div>
        </div>
      </div>

      <div id="kt_app_content" className="app-content flex-column-fluid">
        <div id="kt_app_content_container" className="app-container container-xxl">
          <div className="card">
            <div className="card-header border-0 pt-6">
              <div className="card-title">
                <div className="d-flex align-items-center position-relative my-1">
                  <i className="ki-duotone ki-magnifier fs-3 position-absolute ms-5">
                    <span className="path1"></span>
                    <span className="path2"></span>
                  </i>
                  <input
                    type="text"
                    value={apiData.search}
                    onChange={e => setApiData({ ...apiData, search: e.target.value, page: 1 })}
                    className="form-control form-control-solid w-250px ps-13"
                    placeholder="Search Emirates..."
                  />
                </div>
              </div>
              <div className="card-toolbar">
                <div className="d-flex justify-content-end">
                  <Link to="/emirates/create" className="btn btn-primary">
                    <i className="ki-duotone ki-plus fs-2"></i>
                    Add Emirates
                  </Link>
                </div>
              </div>
            </div>

            <div className="card-body pt-0">
              {pageLoader ? (
                <Loader />
              ) : (
                <div className="table-responsive">
                  <table className="table align-middle table-row-dashed fs-6 gy-5">
                    <thead>
                      <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                        <th>Name</th>
                        <th>Name (Arabic)</th>
                        <th>Code</th>
                        <th>Status</th>
                        <th>Created Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody className="fw-semibold text-gray-600">
                      {emirates.length > 0 ? emirates.map((emirate, index) => (
                        <tr key={index}>
                          <td>{emirate.name}</td>
                          <td>{emirate.name_ar || 'N/A'}</td>
                          <td>
                            <span className="badge bg-primary">{emirate.code}</span>
                          </td>
                          <td>
                            <span className={`badge ${emirate.status ? 'badge-light-success' : 'badge-light-danger'}`}>
                              {emirate.status ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td>{formatDate(emirate.createdAt, 'DD MMM YYYY, hh:mm A')}</td>
                          <td>
                            <Link to={`/emirates/create/${emirate._id}`} className="btn btn-sm h-30px btn-light-primary">
                              <i className="bi bi-pencil-fill fs-6"></i>
                            </Link>
                            <button 
                              className="btn btn-sm h-30px btn-light-danger ms-2"
                              onClick={() => deleteEmirate(emirate._id)}
                            >
                              <i className="bi bi-trash-fill fs-6"></i>
                            </button>
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="6" className="text-center py-4">
                            <div className="text-muted">No emirates found</div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmiratesList;
